import React, { useState, useMemo } from "react";

import { cn } from "@/lib/utils";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import { DiscountsEditModal } from "./DiscountsEditModal";
import { formatShopifyFieldValue, type FormattedValue } from "../../../utils/shopifyFormatting";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { useLanguage } from "@/i18n/useLanguage";

/**
 * Helper to render FormattedValue (handles both string and HTML content)
 */
function renderFormattedValue(value: FormattedValue): React.ReactNode {
  if (typeof value === "object" && "__html" in value) {
    return <span dangerouslySetInnerHTML={value} />;
  }
  return <>{value}</>;
}

/**
 * Processed field item - either combined (amount + percentage) or single field
 */
type ProcessedFieldItem = 
  | {
      type: 'combined';
      label: string;
      amountField: DocFieldSchema | undefined;
      percentageField: DocFieldSchema | undefined;
      allAmountFields?: DocFieldSchema[]; // For summing multiple discount amounts
    }
  | {
      type: 'single';
      field: DocFieldSchema;
    };

/**
 * Discounts Section Widget
 * 
 * Displays discount fields with special layout.
 * Combines amount + percentage pairs into single rows to avoid confusion.
 */
export function DiscountsSection({
  section,
  formData,
  onFieldChange,
  disabled,
  isSaving,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);

  // Get view context for label resolution
  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const { language } = useLanguage();

  const allFields = useMemo(
    () => section.columns.flatMap((col: any) => col.fields),
    [section.columns]
  );

  const { visibleFieldNames } = useFieldDependencies(
    allFields,
    formData
  );

  const fieldsToRender = allFields.filter((field: any) =>
    visibleFieldNames.has(field.fieldname)
  ).filter((field: any) => field.fieldname !== 'name');

  const currency = formData.currency || 'THB';

  // Define discount fields to combine into single "Discount" row
  // All discount fields (regular + additional) are combined into one row
  const discountAmountFields = ['discount_amount', 'additional_discount_amount'];
  const discountPercentageFields = ['discount_percentage', 'additional_discount_percentage'];

  // Process fields - combine all discount fields into single "Discount" row
  const processedFields = useMemo(() => {
    if (fieldsToRender.length === 0) {
      return [];
    }
    const processed: ProcessedFieldItem[] = [];
    const usedFields = new Set<string>();
    
    // Collect all discount amount and percentage fields
    const amountFields: DocFieldSchema[] = [];
    const percentageFields: DocFieldSchema[] = [];
    
    for (const field of fieldsToRender) {
      if (discountAmountFields.includes(field.fieldname)) {
        amountFields.push(field);
        usedFields.add(field.fieldname);
      } else if (discountPercentageFields.includes(field.fieldname)) {
        percentageFields.push(field);
        usedFields.add(field.fieldname);
      }
    }
    
    // Create single combined "Discount" row if we have any discount fields
    if (amountFields.length > 0 || percentageFields.length > 0) {
      // Collect all amount fields (we'll sum them in rendering)
      // Use the first one as the primary field for formatting
      const primaryAmountField = amountFields.find(f => f.fieldname === 'discount_amount') || amountFields[0];
      // Use the first percentage field (prefer discount_percentage over additional_discount_percentage)
      const percentageField = percentageFields.find(f => f.fieldname === 'discount_percentage') || percentageFields[0];
      
      // Resolve label for the combined discount row using the primary amount field
      const resolvedLabel = primaryAmountField 
        ? resolveLabel(doctype, primaryAmountField, mode, style, enableSystemTranslations)
        : null;
      
      processed.push({
        type: 'combined',
        label: resolvedLabel?.label || 'Discount',
        amountField: primaryAmountField,
        percentageField: percentageField,
        // Store all amount fields for summing
        allAmountFields: amountFields
      });
    }
    
    // Add remaining fields (not discount fields) as single fields
    for (const field of fieldsToRender) {
      if (!usedFields.has(field.fieldname)) {
        processed.push({
          type: 'single',
          field: field
        });
        usedFields.add(field.fieldname);
      }
    }
    
    return processed;
  }, [fieldsToRender, formData, doctype, mode, style, enableSystemTranslations, resolveLabel, language]);

  if (processedFields.length === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-2">
        <div
          className={cn(
            CARD,
            !disabled && "cursor-pointer",
            disabled && "cursor-not-allowed"
          )}
          onClick={() => !disabled && setModalOpen(true)}
        >
          <div className="p-6">
            <div className="space-y-3">
              {processedFields.map((item, index) => {
                if (item.type === 'combined') {
                  // Combined display: amount + percentage in same row
                  // Sum all discount amounts if multiple exist
                  let totalAmount = 0;
                  let hasAmount = false;
                  
                  // Check if we have allAmountFields (for summing multiple amounts)
                  const allAmountFields = item.allAmountFields;
                  
                  if (allAmountFields && allAmountFields.length > 0) {
                    // Sum all discount amounts
                    for (const amountField of allAmountFields) {
                      const value = formData[amountField.fieldname];
                      if (value !== null && value !== undefined && value !== "") {
                        const numValue = typeof value === 'string' ? parseFloat(value) : value;
                        if (!isNaN(numValue)) {
                          totalAmount += numValue;
                          hasAmount = true;
                        }
                      }
                    }
                  } else if (item.amountField) {
                    // Single amount field
                    const value = formData[item.amountField.fieldname];
                    if (value !== null && value !== undefined && value !== "") {
                      const numValue = typeof value === 'string' ? parseFloat(value) : value;
                      if (!isNaN(numValue)) {
                        totalAmount = numValue;
                        hasAmount = true;
                      }
                    }
                  }
                  
                  const percentageValue = item.percentageField 
                    ? formData[item.percentageField.fieldname] 
                    : null;
                  
                  // Format amount - make it negative and format
                  let amountDisplay: string | null = null;
                  if (hasAmount && item.amountField && totalAmount > 0) {
                    // Format as negative (even if data is positive)
                    const formatted = formatShopifyFieldValue(item.amountField, totalAmount, currency);
                    if (typeof formatted === 'string') {
                      amountDisplay = `-${formatted}`;
                    } else {
                      // If it's HTML, we need to handle differently
                      const amountStr = String(totalAmount);
                      const symbols: Record<string, string> = {
                        'THB': '฿',
                        'USD': '$',
                        'EUR': '€',
                        'GBP': '£',
                      };
                      const symbol = symbols[currency] || currency;
                      amountDisplay = `-${symbol}${parseFloat(amountStr).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    }
                  }
                  
                  // Format percentage - ensure it has % symbol
                  let percentageDisplay: string | null = null;
                  if (item.percentageField && 
                      percentageValue !== null && 
                      percentageValue !== undefined &&
                      percentageValue !== "") {
                    const numValue = typeof percentageValue === 'string' ? parseFloat(percentageValue) : percentageValue;
                    if (!isNaN(numValue)) {
                      // Format as percentage with % symbol
                      percentageDisplay = `${numValue.toLocaleString('en-US', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}%`;
                    }
                  }
                  
                  // Build combined display value
                  let combinedValue: React.ReactNode = null;
                  
                  if (amountDisplay && percentageDisplay) {
                    // Both present: "-฿100.00 (5.00%)" - both in red
                    combinedValue = (
                      <>
                        <span className="text-red-600 dark:text-red-400">{amountDisplay}</span>{' '}
                        <span className="text-red-600 dark:text-red-400">({percentageDisplay})</span>
                      </>
                    );
                  } else if (amountDisplay) {
                    // Only amount: "-฿100.00" (red)
                    combinedValue = (
                      <span className="text-red-600 dark:text-red-400">{amountDisplay}</span>
                    );
                  } else if (percentageDisplay) {
                    // Only percentage: "(5.00%)" - also in red
                    combinedValue = (
                      <span className="text-red-600 dark:text-red-400">({percentageDisplay})</span>
                    );
                  }
                  
                  // Skip if no value to display
                  if (!combinedValue) return null;
                  
                  return (
                    <div
                      key={`combined:${item.label}:${item.amountField?.fieldname ?? "none"}:${item.percentageField?.fieldname ?? "none"}`}
                      className={cn(
                        "flex items-center justify-between",
                        index < processedFields.length - 1 && "pb-2"
                      )}
                    >
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {item.label}
                      </div>
                      <div className="text-sm font-semibold">
                        {combinedValue}
                      </div>
                    </div>
                  );
                } else {
                  // Single field display (normal)
                  const field = item.field;
                  const value = formData[field.fieldname];
                  
                  // Guard against null/undefined/empty values
                  if (value === null || value === undefined || value === "") {
                    return null;
                  }
                  
                  const displayValue = formatShopifyFieldValue(field, value, currency);
                  const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
                  const isCurrency = field.fieldtype === 'Currency' || 
                    ['amount', 'total', 'net_total', 'total_taxes_and_charges'].includes(field.fieldname);
                  const isPercentage = field.fieldname?.includes('percentage') || 
                    field.fieldtype === 'Percent';

                  return (
                    <div
                      key={field.fieldname}
                      className={cn(
                        "flex items-center justify-between",
                        index < processedFields.length - 1 && "pb-2"
                      )}
                    >
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {resolvedLabel.label}
                      </div>
                      <div className={cn(
                        "text-sm font-medium",
                        (isCurrency || isPercentage) && "font-semibold",
                        "text-gray-900 dark:text-gray-100"
                      )}>
                        {renderFormattedValue(displayValue)}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </div>
      </div>

      <DiscountsEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        fields={allFields}
        formData={formData}
        onFieldChange={onFieldChange}
        disabled={disabled}
        isSaving={isSaving}
        documentName={formData?.name}
      />
    </>
  );
}

